/**
 * Session Manager Context
 * Provides session state and actions to all components
 */

import { message } from 'antd';
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import { authService } from '@/services/auth/authService';
import { sessionMonitor } from '@/services/session/sessionMonitor';
import type { SessionConfig, SessionTimerInfo } from '@/types/session';

interface SessionManagerContextValue {
  // Session state
  isAuthenticated: boolean;
  sessionInfo: SessionTimerInfo;

  // Warning states
  showWarning: boolean;
  warningTimeRemaining: number; // seconds

  // Actions
  extendSession: () => Promise<boolean>;
  logout: () => Promise<void>;
  dismissWarning: () => void;

  // Configuration
  configure: (config: Partial<SessionConfig>) => void;
}

const SessionManagerContext = createContext<SessionManagerContextValue | null>(
  null,
);

export interface SessionManagerProviderProps {
  children: React.ReactNode;
  config?: Partial<SessionConfig>;
  autoStart?: boolean;
}

export const SessionManagerProvider: React.FC<SessionManagerProviderProps> = ({
  children,
  config,
  autoStart = true,
}) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [sessionInfo, setSessionInfo] = useState<SessionTimerInfo>({
    tokenExpiresIn: 0,
    idleTimeRemaining: Infinity,
    isAboutToExpire: false,
    isIdle: false,
  });
  const [showWarning, setShowWarning] = useState(false);
  const [warningTimeRemaining, setWarningTimeRemaining] = useState(0);

  const warningIntervalRef = useRef<NodeJS.Timeout | null>(null);

  /**
   * Update session info periodically
   */
  const updateSessionInfo = useCallback(() => {
    const info = sessionMonitor.getSessionTimerInfo();
    setSessionInfo(info);
    setIsAuthenticated(authService.isAuthenticated());
  }, []);

  /**
   * Handle session expired
   */
  const handleSessionExpired = useCallback(() => {
    if (process.env.NODE_ENV === 'development') {
      console.log('🚨 SessionManager: Session expired');
    }

    setShowWarning(false);

    message.error('Your session has expired. Please log in again.');

    // Logout
    authService.logout();
  }, []);

  /**
   * Handle session warning
   */
  const handleSessionWarning = useCallback(
    (timeRemaining: number) => {
      if (process.env.NODE_ENV === 'development') {
        console.log(
          '⚠️ SessionManager: Session warning -',
          timeRemaining,
          'seconds remaining',
        );
      }

      setShowWarning(true);
      setWarningTimeRemaining(timeRemaining);

      // Start countdown
      if (warningIntervalRef.current) {
        clearInterval(warningIntervalRef.current);
      }

      warningIntervalRef.current = setInterval(() => {
        setWarningTimeRemaining((prev) => {
          const newTime = prev - 1;
          if (newTime <= 0) {
            if (warningIntervalRef.current) {
              clearInterval(warningIntervalRef.current);
              warningIntervalRef.current = null;
            }
            handleSessionExpired();
            return 0;
          }
          return newTime;
        });
      }, 1000);
    },
    [handleSessionExpired],
  );

  /**
   * Handle idle warning
   */
  const handleIdleWarning = useCallback((timeRemaining: number) => {
    if (process.env.NODE_ENV === 'development') {
      console.log(
        '⚠️ SessionManager: Idle warning -',
        timeRemaining,
        'seconds remaining',
      );
    }

    message.warning({
      content: `You've been inactive. Your session will expire in ${Math.ceil(timeRemaining / 60)} minutes.`,
      duration: 5,
    });
  }, []);

  /**
   * Handle token refreshed
   */
  const handleTokenRefreshed = useCallback(() => {
    if (process.env.NODE_ENV === 'development') {
      console.log('✅ SessionManager: Token refreshed');
    }

    updateSessionInfo();
  }, [updateSessionInfo]);

  /**
   * Extend session
   */
  const extendSession = useCallback(async (): Promise<boolean> => {
    const success = await sessionMonitor.extendSession();

    if (success) {
      // Hide warning
      setShowWarning(false);

      // Clear countdown
      if (warningIntervalRef.current) {
        clearInterval(warningIntervalRef.current);
        warningIntervalRef.current = null;
      }

      // Update session info
      updateSessionInfo();

      message.success('Session extended successfully');

      return true;
    } else {
      message.error('Failed to extend session. Please log in again.');
      return false;
    }
  }, [updateSessionInfo]);

  /**
   * Logout
   */
  const logout = useCallback(async (): Promise<void> => {
    setShowWarning(false);

    // Clear countdown
    if (warningIntervalRef.current) {
      clearInterval(warningIntervalRef.current);
      warningIntervalRef.current = null;
    }

    // Stop monitoring
    sessionMonitor.cleanup();

    // Logout
    await authService.logout();
  }, []);

  /**
   * Dismiss warning (user clicked "Continue")
   */
  const dismissWarning = useCallback(() => {
    setShowWarning(false);

    // Clear countdown
    if (warningIntervalRef.current) {
      clearInterval(warningIntervalRef.current);
      warningIntervalRef.current = null;
    }
  }, []);

  /**
   * Configure session monitor
   */
  const configure = useCallback((newConfig: Partial<SessionConfig>) => {
    sessionMonitor.configure(newConfig);
  }, []);

  /**
   * Initialize session monitor
   */
  useEffect(() => {
    // Configure if config provided
    if (config) {
      sessionMonitor.configure(config);
    }

    // Register event handlers
    sessionMonitor.onSessionExpired(handleSessionExpired);
    sessionMonitor.onSessionWarning(handleSessionWarning);
    sessionMonitor.onIdleWarning(handleIdleWarning);
    sessionMonitor.onTokenRefreshed(handleTokenRefreshed);

    // Start monitoring if authenticated and autoStart is true
    if (autoStart && authService.isAuthenticated()) {
      if (process.env.NODE_ENV === 'development') {
        console.log(
          '▶️ SessionManagerContext: Starting monitoring (user already authenticated)',
        );
      }
      sessionMonitor.start();
      updateSessionInfo();
    }

    // Update session info every second for UI
    const infoInterval = setInterval(updateSessionInfo, 1000);

    // Cleanup
    return () => {
      clearInterval(infoInterval);

      if (warningIntervalRef.current) {
        clearInterval(warningIntervalRef.current);
      }
    };
  }, [
    config,
    autoStart,
    handleSessionExpired,
    handleSessionWarning,
    handleIdleWarning,
    handleTokenRefreshed,
    updateSessionInfo,
  ]);

  /**
   * Watch for authentication state changes and start monitoring when user logs in
   * This handles the case where SessionManagerContext initializes before authentication
   */
  useEffect(() => {
    let checkInterval: NodeJS.Timeout;

    if (autoStart) {
      // Check authentication status periodically
      checkInterval = setInterval(() => {
        const nowAuthenticated = authService.isAuthenticated();

        // If user just became authenticated and monitoring isn't started yet
        if (nowAuthenticated && !isAuthenticated) {
          if (process.env.NODE_ENV === 'development') {
            console.log(
              '▶️ SessionManagerContext: User authenticated, starting monitoring',
            );
          }
          sessionMonitor.start();
          updateSessionInfo();
          setIsAuthenticated(true);
        }
        // If user logged out, stop monitoring
        else if (!nowAuthenticated && isAuthenticated) {
          if (process.env.NODE_ENV === 'development') {
            console.log(
              '⏹️ SessionManagerContext: User logged out, stopping monitoring',
            );
          }
          sessionMonitor.stop();
          setIsAuthenticated(false);
        }
      }, 1000); // Check every second
    }

    return () => {
      if (checkInterval) {
        clearInterval(checkInterval);
      }
    };
  }, [autoStart, isAuthenticated, updateSessionInfo]);

  /**
   * Cleanup on unmount
   */
  useEffect(() => {
    return () => {
      sessionMonitor.stop();
    };
  }, []);

  const value: SessionManagerContextValue = {
    isAuthenticated,
    sessionInfo,
    showWarning,
    warningTimeRemaining,
    extendSession,
    logout,
    dismissWarning,
    configure,
  };

  return (
    <SessionManagerContext.Provider value={value}>
      {children}
    </SessionManagerContext.Provider>
  );
};

/**
 * Hook to access session manager
 */
export const useSessionManager = (): SessionManagerContextValue => {
  const context = useContext(SessionManagerContext);

  if (!context) {
    throw new Error(
      'useSessionManager must be used within SessionManagerProvider',
    );
  }

  return context;
};

export default SessionManagerProvider;
